import React, { useState, useEffect } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { 
  LayoutDashboard, 
  CheckSquare, 
  FileText, 
  Calendar, 
  BookOpen, 
  CreditCard,
  Upload,
  Download,
  AlertCircle,
  LogOut,
  User,
  File,
  Image,
  FileType
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { notifyError } from '../../lib/notify';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const navItems = [
  { id: 'overview', label: 'Overview', icon: LayoutDashboard, path: '/portal/dashboard' },
  { id: 'checklist', label: 'Checklist', icon: CheckSquare, path: '/portal/checklist' },
  { id: 'documents', label: 'Documents', icon: FileText, path: '/portal/documents' },
  { id: 'sessions', label: 'Sessions', icon: Calendar, path: '/portal/sessions' },
  { id: 'resources', label: 'Resources', icon: BookOpen, path: '/portal/resources' },
  { id: 'subscription', label: 'Subscription', icon: CreditCard, path: '/portal/subscription' },
];

const categories = ['Visa', 'Banking', 'Real Estate', 'Tax', 'Logistics', 'Other'];

const PortalDocuments = () => {
  const { user, loading, logout } = useAuth();
  const [documents, setDocuments] = useState(null);
  const [dataLoading, setDataLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    if (user) {
      fetchDocuments();
    }
  }, [user]);

  const fetchDocuments = async () => {
    try {
      const response = await fetch(`${API_URL}/api/portal/documents`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setDocuments(data);
      }
    } catch (err) {
      console.error('Error fetching documents:', err);
    } finally {
      setDataLoading(false);
    }
  };

  const handleFileUpload = async (file, category) => {
    setUploading(true);
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('category', category);
    formData.append('doc_type_id', 'general');

    try {
      const response = await fetch(`${API_URL}/api/portal/documents/upload`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });
      
      if (response.ok) {
        fetchDocuments();
      } else {
        const error = await response.json().catch(() => ({}));
        notifyError(error, 'Upload failed.');
      }
    } catch (err) {
      console.error('Upload error:', err);
      notifyError('Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  const downloadDocument = async (docId, filename) => {
    try {
      const response = await fetch(`${API_URL}/api/portal/documents/${docId}/download`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (err) {
      console.error('Download error:', err);
    }
  };

  const StatusBadge = ({ status }) => {
    const styles = {
      pending: 'bg-yellow-500/20 text-yellow-400',
      approved: 'bg-green-500/20 text-green-400',
      needs_revision: 'bg-red-500/20 text-red-400',
    };
    const labels = {
      pending: 'Pending Review',
      approved: 'Approved',
      needs_revision: 'Needs Revision',
    };
    return (
      <span className={`px-2 py-1 text-xs rounded ${styles[status] || styles.pending}`}>
        {labels[status] || status}
      </span>
    );
  };

  const getFileIcon = (mimeType) => {
    if (mimeType?.includes('pdf')) return FileType;
    if (mimeType?.includes('image')) return Image;
    return File;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric', timeZone: 'America/Chicago'});
  };

  const filteredDocuments = selectedCategory === 'all' 
    ? documents?.documents 
    : documents?.documents?.filter(d => d.category === selectedCategory);

  if (loading || dataLoading) {
    return (
      <div className="min-h-screen bg-[#F9F6EF] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#2CA8A0] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/portal" replace />;
  }

  return (
    <div className="min-h-screen bg-[#F9F6EF] flex">
      {/* Sidebar */}
      <aside className="w-64 bg-[#F9F6EF] border-r border-[#FFFFFF] flex flex-col">
        <div className="p-6 border-b border-[#FFFFFF]">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-8 h-8 bg-white flex items-center justify-center">
              <span className="text-black text-xs font-bold">FR</span>
            </div>
            <span className="text-white text-sm font-medium">Client Portal</span>
          </Link>
        </div>
        <nav className="flex-1 py-6">
          <ul className="space-y-1 px-3">
            {navItems.map((item) => (
              <li key={item.id}>
                <Link
                  to={item.path}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded transition-all ${
                    item.id === 'documents'
                      ? 'bg-[#2CA8A0]/10 text-[#2CA8A0]'
                      : 'text-[#888] hover:text-white hover:bg-[#FFFFFF]'
                  }`}
                >
                  <item.icon className="w-5 h-5 shrink-0" />
                  <span className="text-sm">{item.label}</span>
                </Link>
              </li>
            ))}
          </ul>
        </nav>
        <div className="p-4 border-t border-[#FFFFFF]">
          <div className="flex items-center gap-3 mb-3">
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
            ) : (
              <div className="w-8 h-8 bg-[#FFFFFF] rounded-full flex items-center justify-center">
                <User className="w-4 h-4 text-[#666]" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm text-white truncate">{user?.name || 'Client'}</p>
            </div>
          </div>
          <button onClick={logout} className="flex items-center gap-2 text-[#666] hover:text-white text-sm transition">
            <LogOut className="w-4 h-4" />
            <span>Sign Out</span>
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto">
        <div className="p-8 lg:p-12">
          <div className="flex items-start justify-between mb-10">
            <div>
              <h1 className="text-3xl font-light text-white mb-2">Document Vault</h1>
              <p className="text-[#888]">Upload and manage your relocation documents.</p>
            </div>

            {/* Upload button */}
            <label className="cursor-pointer">
              <input
                type="file"
                className="hidden"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => {
                  if (e.target.files?.[0]) {
                    handleFileUpload(e.target.files[0], 'Other');
                  }
                }}
              />
              <span className="inline-flex items-center gap-2 bg-[#2CA8A0] text-black px-5 py-2.5 text-sm font-medium hover:bg-[#2CA8A0] transition-all">
                {uploading ? 'Uploading...' : (
                  <>
                    <Upload className="w-4 h-4" />
                    Upload Document
                  </>
                )}
              </span>
            </label>
          </div>

          {/* Category filter */}
          <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`px-4 py-2 text-sm rounded transition ${
                selectedCategory === 'all'
                  ? 'bg-[#2CA8A0] text-black'
                  : 'bg-[#FFFFFF] text-[#888] hover:text-white'
              }`}
            >
              All ({documents?.total || 0})
            </button>
            {categories.map((cat) => {
              const count = documents?.grouped?.[cat]?.length || 0;
              return (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-4 py-2 text-sm rounded transition whitespace-nowrap ${
                    selectedCategory === cat
                      ? 'bg-[#2CA8A0] text-black'
                      : 'bg-[#FFFFFF] text-[#888] hover:text-white'
                  }`}
                >
                  {cat} ({count})
                </button>
              );
            })}
          </div>

          {/* Documents list */}
          {filteredDocuments && filteredDocuments.length > 0 ? (
            <div className="space-y-3">
              {filteredDocuments.map((doc) => {
                const FileIcon = getFileIcon(doc.mime_type);
                return (
                  <div
                    key={doc.doc_id}
                    className="flex items-center justify-between p-5 bg-[#F9F6EF] border border-[#FFFFFF] hover:border-[#2a2a2a] transition"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-[#FFFFFF] flex items-center justify-center rounded">
                        <FileIcon className="w-5 h-5 text-[#666]" />
                      </div>
                      <div>
                        <p className="text-white font-medium mb-1">{doc.filename}</p>
                        <div className="flex items-center gap-3 text-xs text-[#666]">
                          <span>{doc.category}</span>
                          <span>·</span>
                          <span>{formatDate(doc.uploaded_at)}</span>
                          {doc.file_size && (
                            <>
                              <span>·</span>
                              <span>{Math.round(doc.file_size / 1024)} KB</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      <StatusBadge status={doc.review_status} />
                      <button
                        onClick={() => downloadDocument(doc.doc_id, doc.filename)}
                        className="flex items-center gap-1 text-sm text-[#888] hover:text-white transition"
                      >
                        <Download className="w-4 h-4" />
                        Download
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-16 bg-[#F9F6EF] border border-[#FFFFFF]">
              <FileText className="w-12 h-12 text-[#333] mx-auto mb-4" />
              <p className="text-white mb-2">No documents yet</p>
              <p className="text-[#666] text-sm">
                Upload documents from your checklist or use the button above.
              </p>
            </div>
          )}

          {/* Revision notes */}
          {filteredDocuments?.some(d => d.review_status === 'needs_revision' && d.reviewer_notes) && (
            <div className="mt-8 p-5 bg-red-500/10 border border-red-500/20 rounded">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
                <div>
                  <p className="text-red-400 font-medium mb-2">Documents Need Revision</p>
                  {filteredDocuments
                    .filter(d => d.review_status === 'needs_revision' && d.reviewer_notes)
                    .map(doc => (
                      <div key={doc.doc_id} className="mb-2 last:mb-0">
                        <p className="text-white text-sm">{doc.filename}</p>
                        <p className="text-[#888] text-sm">{doc.reviewer_notes}</p>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default PortalDocuments;
